<template>
  <div class="tile-inputs flex flex-col space-y-4">
    <div v-if="contentConfig.image === 'illustration'">
      <div class="mb-1 text-xs font-semibold text-gray-500 uppercase">
        {{ $t("BORROWER_PLATFORM.ADD_BLOCK_MENU.ILLUSTRATION") }}
      </div>
      <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        <div
          v-for="illustration in availableIllustrations"
          :key="illustration.name"
          class="illustration-item flex flex-col items-center justify-center rounded-md border p-2 transition-all"
          :class="[
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            {
              'border-gray-200 hover:border-blue-500':
                selectedIcon !== illustration.name && !disabled,
              'border-2 border-blue-500': selectedIcon === illustration.name,
            },
          ]"
          @click="selectIllustration(illustration.name)"
        >
          <component :is="illustration.component" class="h-12 w-12" />
        </div>
      </div>
    </div>

    <div v-else-if="contentConfig.image === 'image'" class="space-y-2">
      <div class="mb-1 text-xs font-semibold text-gray-500 uppercase">
        {{ $t("BORROWER_PLATFORM.ADD_BLOCK_MENU.IMAGE") }}
      </div>
      <template v-if="hasImage">
        <div class="flex flex-col items-center gap-3">
          <div
            class="group relative h-32 w-full overflow-hidden rounded-md border border-gray-200"
          >
            <img
              :src="fileUrl"
              alt="uploaded-image"
              class="h-full w-full object-cover"
            />
            <div
              class="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            >
              <button
                v-if="!disabled"
                type="button"
                class="bg-opacity-80 hover:bg-opacity-100 absolute top-2 left-2 flex items-center gap-1 rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-600 transition-colors"
                @click="() => openFileDialog()"
              >
                {{ $t("COMMON.EDIT") }}
              </button>
              <button
                v-if="!disabled"
                type="button"
                class="bg-opacity-80 hover:bg-opacity-100 absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-gray-600 transition-colors"
                @click="handleDeleteImage"
              >
                <icon-base :icon="IconX" width="10" height="10" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <upload-box
          v-if="!disabled"
          accept=".png,.gif,.jpeg,.jpg"
          class="h-32"
          @files:add="handleFilesAdd"
        />
        <div
          v-else
          class="flex h-32 items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500"
        >
          {{ $t("BORROWER_PLATFORM.NO_IMAGE_SELECTED") }}
        </div>
      </template>
      <div
        v-if="contentConfig.image === 'image' && hasImage && !disabled"
        class="flex items-start justify-between py-4"
      >
        <div class="w-26">
          <lf-input
            v-if="!isFullWidth"
            :value="scale"
            type="number"
            :min="1"
            :max="100"
            :step="1"
            :placeholder="$t('BORROWER_PLATFORM.SCALE_PERCENT')"
            :disabled="disabled"
            name="tile-scale-input"
            data-cy="tile-scale-input"
            @keydown="handleScaleKeydown"
            @key-released="handleScaleChangeBase($event, 1, 100, 1)"
          />
          <lf-input
            v-else
            value=""
            :disabled="true"
            class="pointer-events-none invisible"
            name="placeholder-input"
          />
        </div>

        <div class="mt-2 flex items-center space-x-2">
          <span>
            {{ $t("BORROWER_PLATFORM.FULL_WIDTH") }}
          </span>
          <lf-switch
            :model-value="isFullWidth"
            :disabled="disabled"
            name="tile-full-width-toggle"
            data-cy="tile-full-width-toggle"
            @update:model-value="handleFullWidthToggle"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col space-y-4">
      <div class="mb-3 text-xs font-semibold text-gray-500 uppercase">
        {{ $t("COMMON.DESCRIPTION") }}
      </div>
      <div v-if="contentConfig.header !== 'none'">
        <lf-input
          :value="tile.title || ''"
          :placeholder="t('BORROWER_PLATFORM.TEMPLATE_EDITOR.TITLE')"
          :disabled="disabled"
          name="tile-title"
          @change="handleTitleChange"
        />
      </div>

      <div>
        <lf-text-area
          :value="tile.description || ''"
          :maxlength="500"
          :character-limit="500"
          :placeholder="t('COMMON.DESCRIPTION')"
          :disabled="disabled"
          name="tile-description"
          @change="handleDescriptionChange"
        />
      </div>
    </div>

    <div v-if="contentConfig.button !== 'none'">
      <div class="mb-3 text-xs font-semibold text-gray-500 uppercase">
        {{
          contentConfig.button === "redirect_link"
            ? $t("COMMON.LINK")
            : $t("COMMON.BUTTON")
        }}
      </div>
      <lf-input
        :value="tile.action.text || ''"
        :placeholder="
          contentConfig.button === 'button'
            ? t('BORROWER_PLATFORM.BUTTON_TEXT')
            : t('BORROWER_PLATFORM.LINK_TEXT')
        "
        :disabled="disabled"
        name="tile-action-text"
        @change="handleActionTextChange"
      />

      <div class="mt-2">
        <lf-input
          :value="tile.action.url || ''"
          :placeholder="t('BORROWER_PLATFORM.REDIRECT_LINK')"
          :disabled="disabled"
          name="tile-action-url"
          @change="handleActionUrlChange"
        />
      </div>
    </div>

    <lf-modal v-if="showImageEditModal" :close="handleModalCancel">
      <template #content>
        <div class="flex flex-col items-center gap-4">
          <ImageEditor
            v-if="pendingFile"
            :avatar="pendingFile"
            size="large"
            :preview="false"
            @save="handleImageSave"
            @cancel="handleModalCancel"
          />
        </div>
      </template>
    </lf-modal>
  </div>
</template>

<script setup lang="ts">
import { useFileDialog } from "@vueuse/core";
import { computed, markRaw, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDimensions } from "@/composables/useDimensions";
import { useQueuedFileDeletion } from "@/composables/useQueuedFileDeletion";
import { handleScaleKeydown } from "@/helpers/inputValidation";
import {
  useBorrowerPlatformTemplateFileQuery,
  useUploadBorrowerPlatformTemplateFileMutation,
} from "@/hooks/queries/useBorrowerPlatformTemplates";

import IllustrationCalendar from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationCalendar.vue";
import IllustrationCard from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationCard.vue";
import IllustrationDocumentAndPen from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationDocumentAndPen.vue";
import IllustrationDocuments from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationDocuments.vue";
import IllustrationEncryptedDoc from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationEncryptedDoc.vue";
import IllustrationGraph from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationGraph.vue";
import IllustrationLetter2 from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationLetter2.vue";
import IllustrationLetter from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationLetter.vue";
import IllustrationPeople from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationPeople.vue";
import IllustrationPieChart from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationPieChart.vue";
import IllustrationReport2 from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationReport2.vue";
import IllustrationReport3 from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationReport3.vue";
import IllustrationReport from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationReport.vue";
import IllustrationSearch from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationSearch.vue";
import IllustrationStar from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationStar.vue";
import IllustrationTickets from "@/components/icons/borrowerPlatform/borrowerTabs/illustrations/IllustrationTickets.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ImageEditor from "@/components/ui/ImageEditor.vue";
import LfModal from "@/components/ui/Modal.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";
import LfTextArea from "@/components/ui/inputs/LfTextarea.vue";
import UploadBox from "@/components/ui/inputs/UploadBox.vue";

import type { Tile } from "@/models/borrowerPlatformTemplates";

interface TileContentConfig {
  image: "illustration" | "image" | "none";
  header: "h1" | "h2" | "h3" | "h4" | "h5" | "none";
  button: "button" | "redirect_link" | "none";
}

interface Props {
  tile: Tile;
  contentConfig: TileContentConfig;
  templateId?: string;
  disabled?: boolean;
}

const props = defineProps<Props>();
const { disabled = false } = props;

const emit = defineEmits<{
  "tile:updated": [updatedTile: Tile];
}>();

const { t } = useI18n();
const uploadFileMutation = useUploadBorrowerPlatformTemplateFileMutation();
const { queueFileDeletion } = useQueuedFileDeletion();

const pendingFile = ref<File | undefined>();
const showImageEditModal = ref(false);

const { open: openFileDialog, onChange } = useFileDialog({
  accept: "image/*",
  directory: false,
});

const availableIllustrations = [
  { name: "calendar", component: markRaw(IllustrationCalendar) },
  { name: "card", component: markRaw(IllustrationCard) },
  { name: "document_and_pen", component: markRaw(IllustrationDocumentAndPen) },
  { name: "documents", component: markRaw(IllustrationDocuments) },
  { name: "encrypted_doc", component: markRaw(IllustrationEncryptedDoc) },
  { name: "graph", component: markRaw(IllustrationGraph) },
  { name: "letter", component: markRaw(IllustrationLetter) },
  { name: "letter2", component: markRaw(IllustrationLetter2) },
  { name: "people", component: markRaw(IllustrationPeople) },
  { name: "pie_chart", component: markRaw(IllustrationPieChart) },
  { name: "report", component: markRaw(IllustrationReport) },
  { name: "report2", component: markRaw(IllustrationReport2) },
  { name: "report3", component: markRaw(IllustrationReport3) },
  { name: "search", component: markRaw(IllustrationSearch) },
  { name: "star", component: markRaw(IllustrationStar) },
  { name: "tickets", component: markRaw(IllustrationTickets) },
] as const;

const fileId = computed(() => String(props.tile.image.file_id || ""));

const { data: fileData } = useBorrowerPlatformTemplateFileQuery(
  props.templateId || "",
  fileId
);

const fileUrl = computed(() => {
  return fileData.value?.file_url || "";
});

const selectedIcon = computed(() => props.tile.image.icon);
const hasImage = computed(() => !!fileUrl.value);

const tileImageWrapper = computed(() => props.tile.image);
const updateTileImage = (updates: Partial<typeof props.tile.image>) => {
  emit("tile:updated", {
    ...props.tile,
    image: {
      ...props.tile.image,
      ...updates,
    },
  });
};

const {
  isFullWidth,
  scale,
  handleFullWidthToggle: handleFullWidthToggleBase,
  handleScaleChange: handleScaleChangeBase,
} = useDimensions(tileImageWrapper, updateTileImage, 100);

const selectIllustration = (iconName: string) => {
  if (disabled) {
    return;
  }
  emit("tile:updated", {
    ...props.tile,
    image: {
      ...props.tile.image,
      icon: iconName,
    },
  });
};

const handleTitleChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit("tile:updated", {
    ...props.tile,
    title: value,
  });
};

const handleDescriptionChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit("tile:updated", {
    ...props.tile,
    description: value,
  });
};

const handleActionTextChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit("tile:updated", {
    ...props.tile,
    action: {
      ...props.tile.action,
      text: value,
    },
  });
};

const handleActionUrlChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit("tile:updated", {
    ...props.tile,
    action: {
      ...props.tile.action,
      url: value,
    },
  });
};

const handleFilesAdd = (files: FileList) => {
  if (!files?.length) {
    return;
  }
  pendingFile.value = files[0];
  showImageEditModal.value = true;
};

onChange((files) => {
  if (files?.length) {
    handleFilesAdd(files);
  }
});

const handleImageSave = async (croppedFile: File) => {
  if (!props.templateId) {
    return;
  }

  const uploadResult = await uploadFileMutation.mutateAsync({
    templateId: props.templateId,
    file: croppedFile,
  });

  if (!uploadResult) {
    return;
  }

  emit("tile:updated", {
    ...props.tile,
    image: {
      ...props.tile.image,
      file_id: parseInt(uploadResult.id, 10),
    },
  });

  showImageEditModal.value = false;
  pendingFile.value = undefined;
};

const handleModalCancel = () => {
  showImageEditModal.value = false;
  pendingFile.value = undefined;
};

const handleDeleteImage = () => {
  if (!props.tile.image.file_id || !props.templateId) {
    return;
  }

  queueFileDeletion(props.templateId, String(props.tile.image.file_id));

  emit("tile:updated", {
    ...props.tile,
    image: {
      ...props.tile.image,
      file_id: undefined,
    },
  });
};

const handleFullWidthToggle = handleFullWidthToggleBase;
</script>
